import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import { Send, CheckCircle2, AlertCircle, Mail, User, MessageSquare } from "lucide-react";

const Contact = () => {
  const formRef = useRef();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState(""); // 'sending', 'success', 'error'
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.subject.trim()) newErrors.subject = "Subject is required";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear error for this field when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setStatus("sending");

    // EmailJS configuration - Note: User needs to set up their own EmailJS account
    // For now, sending directly to email using a mailto fallback
    try {
      // Construct mailto link as fallback
      const mailtoLink = `mailto:theankitojha@gmail.com?subject=${encodeURIComponent(
        formData.subject
      )}&body=${encodeURIComponent(
        `From: ${formData.name} (${formData.email})\n\n${formData.message}`
      )}`;

      // Open mailto link
      window.location.href = mailtoLink;

      // Simulate success after a short delay
      setTimeout(() => {
        setStatus("success");
        setFormData({ name: "", email: "", subject: "", message: "" });
        setTimeout(() => setStatus(""), 5000);
      }, 1000);
    } catch (error) {
      setStatus("error");
      setTimeout(() => setStatus(""), 5000);
    }
  };

  return (
    <section
      id="contact"
      style={{
        padding: "100px 7.6923%",
        background: "var(--bg-primary)",
        borderTop: "1px solid var(--border-subtle)",
      }}
    >
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
        }}
      >
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{
            textAlign: "center",
            marginBottom: "60px",
          }}
        >
          <h2
            style={{
              fontSize: "48px",
              fontWeight: 600,
              color: "var(--text-primary)",
              marginBottom: "16px",
            }}
          >
            Secure Message Portal
          </h2>
          <p style={{ fontSize: "18px", color: "var(--text-muted)" }}>
            Send an encrypted message through this secure channel
          </p>
        </motion.div>

        {/* Contact Form */}
        <motion.form
          ref={formRef}
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="glass"
          style={{
            padding: "40px",
            borderRadius: "0px",
            border: "1px solid var(--border-medium)",
          }}
        >
          {/* Name Field */}
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "16px",
                color: "var(--text-secondary)",
                marginBottom: "8px",
                fontWeight: 500,
              }}
            >
              <User size={18} />
              Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your full name"
              style={{
                width: "100%",
                padding: "14px 16px",
                background: "rgba(255, 255, 255, 0.05)",
                border: errors.name ? "1px solid var(--error)" : "1px solid var(--border-subtle)",
                borderRadius: "0px",
                fontSize: "16px",
                color: "var(--text-primary)",
                outline: "none",
                transition: "all 0.3s ease",
              }}
              onFocus={(e) => (e.target.style.borderColor = "var(--brand-primary)")}
              onBlur={(e) => (e.target.style.borderColor = errors.name ? "var(--error)" : "var(--border-subtle)")}
            />
            {errors.name && (
              <span style={{ fontSize: "14px", color: "var(--error)", marginTop: "4px", display: "block" }}>
                {errors.name}
              </span>
            )}
          </div>

          {/* Email Field */}
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "16px",
                color: "var(--text-secondary)",
                marginBottom: "8px",
                fontWeight: 500,
              }}
            >
              <Mail size={18} />
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your.email@example.com"
              style={{
                width: "100%",
                padding: "14px 16px",
                background: "rgba(255, 255, 255, 0.05)",
                border: errors.email ? "1px solid var(--error)" : "1px solid var(--border-subtle)",
                borderRadius: "0px",
                fontSize: "16px",
                color: "var(--text-primary)",
                outline: "none",
                transition: "all 0.3s ease",
              }}
              onFocus={(e) => (e.target.style.borderColor = "var(--brand-primary)")}
              onBlur={(e) => (e.target.style.borderColor = errors.email ? "var(--error)" : "var(--border-subtle)")}
            />
            {errors.email && (
              <span style={{ fontSize: "14px", color: "var(--error)", marginTop: "4px", display: "block" }}>
                {errors.email}
              </span>
            )}
          </div>

          {/* Subject Field */}
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "16px",
                color: "var(--text-secondary)",
                marginBottom: "8px",
                fontWeight: 500,
              }}
            >
              <MessageSquare size={18} />
              Subject
            </label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="What's this about?"
              style={{
                width: "100%",
                padding: "14px 16px",
                background: "rgba(255, 255, 255, 0.05)",
                border: errors.subject ? "1px solid var(--error)" : "1px solid var(--border-subtle)",
                borderRadius: "0px",
                fontSize: "16px",
                color: "var(--text-primary)",
                outline: "none",
                transition: "all 0.3s ease",
              }}
              onFocus={(e) => (e.target.style.borderColor = "var(--brand-primary)")}
              onBlur={(e) => (e.target.style.borderColor = errors.subject ? "var(--error)" : "var(--border-subtle)")}
            />
            {errors.subject && (
              <span style={{ fontSize: "14px", color: "var(--error)", marginTop: "4px", display: "block" }}>
                {errors.subject}
              </span>
            )}
          </div>

          {/* Message Field */}
          <div style={{ marginBottom: "32px" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "16px",
                color: "var(--text-secondary)",
                marginBottom: "8px",
                fontWeight: 500,
              }}
            >
              <MessageSquare size={18} />
              Message
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Your message here..."
              rows="6"
              style={{
                width: "100%",
                padding: "14px 16px",
                background: "rgba(255, 255, 255, 0.05)",
                border: errors.message ? "1px solid var(--error)" : "1px solid var(--border-subtle)",
                borderRadius: "0px",
                fontSize: "16px",
                color: "var(--text-primary)",
                outline: "none",
                resize: "vertical",
                fontFamily: "inherit",
                transition: "all 0.3s ease",
              }}
              onFocus={(e) => (e.target.style.borderColor = "var(--brand-primary)")}
              onBlur={(e) => (e.target.style.borderColor = errors.message ? "var(--error)" : "var(--border-subtle)")}
            />
            {errors.message && (
              <span style={{ fontSize: "14px", color: "var(--error)", marginTop: "4px", display: "block" }}>
                {errors.message}
              </span>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === "sending"}
            className="btn-primary"
            style={{
              width: "100%",
              justifyContent: "center",
              opacity: status === "sending" ? 0.7 : 1,
              cursor: status === "sending" ? "not-allowed" : "pointer",
            }}
          >
            {status === "sending" ? (
              "Sending..."
            ) : (
              <>
                <Send size={20} />
                Send Secure Message
              </>
            )}
          </button>

          {/* Status Messages */}
          {status === "success" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                marginTop: "20px",
                padding: "16px",
                background: "rgba(0, 255, 136, 0.1)",
                border: "1px solid var(--success)",
                borderRadius: "0px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <CheckCircle2 size={20} color="var(--success)" />
              <span style={{ fontSize: "16px", color: "var(--success)" }}>
                Message sent successfully! I'll get back to you soon.
              </span>
            </motion.div>
          )}

          {status === "error" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                marginTop: "20px",
                padding: "16px",
                background: "rgba(255, 59, 48, 0.1)",
                border: "1px solid var(--error)",
                borderRadius: "0px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <AlertCircle size={20} color="var(--error)" />
              <span style={{ fontSize: "16px", color: "var(--error)" }}>
                Failed to send message. Please try again or email directly at theankitojha@gmail.com
              </span>
            </motion.div>
          )}
        </motion.form>

        {/* Direct Contact Info */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          style={{
            marginTop: "40px",
            textAlign: "center",
            fontSize: "16px",
            color: "var(--text-muted)",
          }}
        >
          Or reach me directly at{" "}
          <a
            href="mailto:theankitojha@gmail.com"
            style={{
              color: "var(--brand-primary)",
              fontWeight: 500,
            }}
          >
            theankitojha@gmail.com
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
